import { ButtonPrimary } from "@/shared/components/atoms/ButtonPrimary";
import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import TabsUI, { type TabItem } from "@/shared/components/atoms/TabsUI";

import { Select } from "@/shared/components/organisms/Select";
import TableListRecovery from "@/shared/components/organisms/TableListRecovery";
import { faCloudArrowDown, faFilter } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFormik } from "formik";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetAllRecoveryOldQuery,
  useGetAllRecoveryQuery,
  useGetRecoveryCountsQuery,
  useGetUserMarketingTagsQuery,
  useRecoveryExportMutation,
} from "./api/recovery.api.";
import { useSelector } from "react-redux";
import { selectUser } from "@/app/auth/auth.slice";
import TableListRecoveryOld from "@/shared/components/organisms/TableListRecoveryOld";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { X } from "lucide-react";
import { MultiSelect } from "@/shared/components/organisms/MultiSelect";
import IncidenceModal from "@/shared/components/organisms/IncidenceModal";
import MassiveTaskDialog from "@/shared/components/organisms/MassiveTaskDialog";
import { useGetSupportStaffTicketsQuery } from "../TicketPage/api/Ticket.api";
import { t } from "i18next";
import { useFetchGymsMutation } from "@/app/gyms/gyms.api";
import { useBranchsGymQuery as useBranchesGymQuery } from "@/app/branchs/branchs.api";
import { Permission } from "@/shared/hooks/usePermission";
import { useDebounce } from "@/shared/hooks/debounce";

const TABS: TabItem[] = [
  {
    id: "recovery_latest",
    name: "Renovaciones retrasadas (hasta 30 días)",
    permission: Permission.BRANCH_ACCESS_RECOVERY_LATEST,
  },
  {
    id: "recovery_old",
    name: "Bajas antiguas",
    permission: Permission.BRANCH_ACCESS_RECOVERY_OLD,
  },
];

const genderOptions = [
  { id: undefined, name: "Todos" },
  { id: "Hombre", name: "Masculino" },
  { id: "Mujer", name: "Femenino" },
];

const paymentInfoOptions = [
  { id: "delayed", name: "Con retraso" },
  { id: "payment_declined", name: "Cobro rechazado" },
];

interface FormikInitialValuesProps {
  currentTab: "recovery_latest" | "recovery_old";
  gym: { id: number | string; name: string };
  branch: { id: number | string; name: string };
  userGymId: string;
  search: string;
  userBranchId: string;
  dateEnd: string;
  dateInit: string;
  isOpenFilter: boolean;
  selectedGender: string;
  selectedPaymentInfo: string;
  delayDays: string;
  maxAge: string;
  minimalAge: string;
  selectedUserTags: Array<{ id: string | number; name: string }>;
  selectedMarketingTags: Array<{ id: string | number; name: string }>;
}
const initialValues: FormikInitialValuesProps = {
  currentTab: "recovery_latest",
  branch: { id: "", name: "" },
  search: "",
  gym: { id: "", name: "" },
  userGymId: "",
  dateEnd: "",
  dateInit: "",
  userBranchId: "",
  isOpenFilter: false,
  selectedGender: "",
  selectedPaymentInfo: "",
  delayDays: "",
  maxAge: "",
  minimalAge: "",
  selectedUserTags: [],
  selectedMarketingTags: [],
};

export const RecoveryPage = () => {
  const [currentIncidence, setCurrentIncidence] = useState<any>(null);
  const [shoModalIncidence, setShoModalIncidence] = useState<boolean>(false);
  const [showMassiveTaskDialog, setShowMassiveTaskDialog] =
    useState<boolean>(false);
  const navigate = useNavigate();
  const { id: branchId } = useParams<{ id: string }>();
  const user = useSelector(selectUser);

  const formik = useFormik<FormikInitialValuesProps>({
    initialValues: {
      ...initialValues,
      userBranchId: user?.branch?.id,
      userGymId: user?.branch?.gymId,
    },
    onSubmit: () => {},
    enableReinitialize: true,
  });
  const debounceValues = useDebounce(formik.values);
  const { currentTab, gym, userBranchId, userGymId } = formik.values;
  const gymIdToUse = gym.id || userGymId;
  const supportStaffQuery = useGetSupportStaffTicketsQuery({});
  const [recoveryExport] = useRecoveryExportMutation();
  const tagsQuery = useGetUserMarketingTagsQuery(undefined);
  const recoveryCountsQuery = useGetRecoveryCountsQuery(undefined);
  const {
    data: dataRecovery,
    isLoading: isLoadingRecovery,
    isFetching: isFetchingRecovery,
  } = useGetAllRecoveryQuery(
    {
      filial_id: branchId,
      search: debounceValues.search || null,
      gymId: debounceValues.gym.id || null,
      branchId: debounceValues.branch.id || null,
      sex: debounceValues.selectedGender || null,
      initialAge: debounceValues.minimalAge || null,
      finalAge: debounceValues.maxAge || null,
      daysLate: debounceValues.delayDays || null,
      statePayment: debounceValues.selectedPaymentInfo,
      userTags:
        debounceValues.selectedUserTags.length > 0
          ? debounceValues.selectedUserTags
          : null,
      marketingTags:
        debounceValues.selectedMarketingTags.length > 0
          ? debounceValues.selectedMarketingTags
          : null,
    },
    {
      skip: currentTab !== "recovery_latest",
    }
  );
  const {
    data: dataRecoveryOld,
    isLoading: isLoadingRecoveryOld,
    isFetching: isFetchingRecoveryOld,
  } = useGetAllRecoveryOldQuery(
    {
      filial_id: branchId,
      search: debounceValues.search || null,
      gymId: debounceValues.gym.id || null,
      branchId: debounceValues.branch.id || null,
      sex: debounceValues.selectedGender || null,
      initialAge: debounceValues.minimalAge || null,
      finalAge: debounceValues.maxAge || null,
      dateInit: debounceValues.dateInit || null,
      dateEnd: debounceValues.dateEnd || null,
      userTags:
        debounceValues.selectedUserTags.length > 0
          ? debounceValues.selectedUserTags.map((tag) => tag.name)
          : null,
      marketingTags:
        debounceValues.selectedMarketingTags.length > 0
          ? debounceValues.selectedMarketingTags.map((tag) => tag.name)
          : null,
    },
    {
      skip: currentTab !== "recovery_old",
    }
  );
  const [gyms, { data }] = useFetchGymsMutation();
  const {
    data: branchesByGym,
    isLoading: isLoadingBranches,
    isFetching: isFetchingBranches,
  } = useBranchesGymQuery(
    {
      branchId: gymIdToUse,
    },
    {
      skip: !gymIdToUse,
    }
  );

  useEffect(() => {
    gyms({
      limit: 1000,
    }).unwrap();
  }, []);

  //helper fn to change formik values
  const handleChangeValue = useCallback((field: string, value: any) => {
    formik.setFieldValue(field, value);
  }, []);

  const peopleToDisplay = useMemo(() => {
    if (currentTab === "recovery_latest") {
      return dataRecovery;
    } else {
      return dataRecoveryOld;
    }
  }, [dataRecovery, dataRecoveryOld, currentTab]);

  const supportStaff = useMemo(() => {
    if (!supportStaffQuery.data) return [];

    const data: Array<any> = supportStaffQuery.data?.data.map((support) => ({
      ...support,
      avatar:
        support?.avatar ??
        "https://dash.fitmewise.com/uploads/users/profile.png",
    }));

    data.unshift({
      id: 0,
      name: t("all_status") || "Todos los estados",
    });

    return data;
  }, [supportStaffQuery.data]);

  const gymOptions = useMemo(() => {
    if (!data?.data) return [];

    return data?.data?.map((gym) => ({
      id: gym.id,
      name: gym.gym,
    }));
  }, [data?.data]);

  const { userMarketingTagOptions, userTagOptions } = useMemo(() => {
    const userTagOptions =
      tagsQuery.data
        ?.filter((tag) => tag.type === 2)
        .map((tag) => ({
          name: tag.name,
          id: tag.id,
        })) || [];
    const userMarketingTagOptions =
      tagsQuery.data
        ?.filter((tag) => tag.type === 1)
        .map((tag) => ({
          name: tag.name,
          id: tag.id,
        })) || [];

    return {
      userTagOptions,
      userMarketingTagOptions,
    };
  }, [tagsQuery.data]);

  const optionBranches = useMemo(() => {
    if (!branchesByGym) return [];

    return branchesByGym.map((branch) => ({
      ...branch,
      name: branch.branch,
      id: branch.id,
      gym_id: branch.gym_id,
    }));
  }, [branchesByGym]);

  return (
    <div className="overflow-hidden">
      <>
        <div className="px-4 py-5">
          <TabsUI
            tabs={TABS}
            current={currentTab}
            handleChange={(newTab: string) => {
              formik.setFieldValue("currentTab", newTab);
            }}
          />

          {currentTab === "recovery_latest" ? (
            <div className="mt-2">
              <TableHeaderRecovery
                formikValues={formik.values}
                handleChangeValue={handleChangeValue}
                primary_text_button="Incidencias"
                secondary_text_button="Tareas"
                handleClickPrimaryButton={() => navigate("/tickets")}
                handleClickSecondaryButton={() => navigate("/tasks")}
                exportData={() => {}}
                genderOptions={genderOptions}
                paymentInfoOptions={paymentInfoOptions}
                branchOptions={optionBranches}
                companyOptions={gymOptions}
                isLoadingBranches={isLoadingBranches || isFetchingBranches}
                formikSetFieldValue={formik.setFieldValue}
                userMarketingTagOptions={userMarketingTagOptions}
                userTagOptions={userTagOptions}
                ticketCount={
                  recoveryCountsQuery.data?.data?.tickets_open ?? null
                }
                taskCount={
                  recoveryCountsQuery.data?.data?.leads_tasks_count ?? null
                }
              />
              <TableListRecovery
                loader={isLoadingRecovery || isFetchingRecovery}
                people={peopleToDisplay}
                openIncidence={(ticket: any) => {
                  // Ensure `ticket` is typed correctly
                  setCurrentIncidence(ticket); // Store the specific ticket
                  setShoModalIncidence(true); // Open the incidence modal
                }}
              />
            </div>
          ) : null}

          {currentTab === "recovery_old" ? (
            <div className="mt-2">
              <TableHeaderRecovery
                formikValues={formik.values}
                handleChangeValue={handleChangeValue}
                primary_text_button="Tarea Masiva"
                secondary_text_button="Tareas"
                handleClickPrimaryButton={() => setShowMassiveTaskDialog(true)}
                handleClickSecondaryButton={() => navigate("/tasks")}
                exportData={() => {}}
                genderOptions={genderOptions}
                paymentInfoOptions={paymentInfoOptions}
                branchOptions={optionBranches}
                companyOptions={gymOptions}
                isLoadingBranches={isLoadingBranches || isFetchingBranches}
                formikSetFieldValue={formik.setFieldValue}
                userMarketingTagOptions={userMarketingTagOptions}
                userTagOptions={userTagOptions}
                taskCount={null}
                ticketCount={null}
              />
              <TableListRecoveryOld
                people={peopleToDisplay}
                loader={isLoadingRecoveryOld || isFetchingRecoveryOld}
              />
            </div>
          ) : null}
        </div>
        <IncidenceModal
          open={shoModalIncidence} // Controls visibility of IncidenceModal
          setOpen={setShoModalIncidence} // Allows IncidenceModal to close itself
          data={currentIncidence} // Pass the specific ticket data
          usersSupportStaff={supportStaff}
        />

        <MassiveTaskDialog
          open={showMassiveTaskDialog} // Controls visibility of MassiveTaskDialog
          setOpen={setShowMassiveTaskDialog}
          branch_id_context={Number(branchId)}
        />
      </>
    </div>
  );
};

interface TableHeaderRecoveryProps {
  formikValues: any;
  secondary_text_button: string;
  primary_text_button: string;
  genderOptions: any;
  paymentInfoOptions: any;
  companyOptions: Array<{ id: string | number; name: string }>;
  branchOptions: Array<{ id: string | number; name: string }>;
  userTagOptions: Array<{ id: string | number; name: string }>;
  userMarketingTagOptions: Array<{ id: string | number; name: string }>;
  isLoadingBranches?: boolean;
  ticketCount: number | null;
  taskCount: number | null;
  handleClickPrimaryButton: () => void;
  handleClickSecondaryButton: () => void;
  handleChangeValue: (field: string, value: any) => void;
  exportData: () => void;
  formikSetFieldValue: (field: string, value: any) => void;
}

const TableHeaderRecovery = ({
  formikValues,
  secondary_text_button,
  primary_text_button,
  genderOptions,
  paymentInfoOptions,
  branchOptions,
  companyOptions,
  isLoadingBranches,
  userTagOptions,
  userMarketingTagOptions,
  exportData,
  handleClickPrimaryButton,
  handleClickSecondaryButton,
  handleChangeValue,
  formikSetFieldValue,
  taskCount,
  ticketCount,
}: TableHeaderRecoveryProps) => {
  const {
    search,
    gym: selectedGym,
    branch: selectedBranch,
    userGymId,
    userBranchId,
    isOpenFilter,
    selectedGender,
    selectedPaymentInfo,
    currentTab,
    delayDays,
    maxAge,
    minimalAge,
    selectedUserTags,
    selectedMarketingTags,
  } = formikValues;

  const selectedGymMemo = useMemo(() => {
    const gymIdToUse = selectedGym.id || userGymId;
    return companyOptions.find((gym) => gym.id === gymIdToUse);
  }, [selectedGym.id, userGymId, companyOptions]);

  const selectedBranchMemo = useMemo(() => {
    const branchIdToUse = selectedBranch.id || userBranchId;
    return branchOptions.find((branch) => branch.id === branchIdToUse);
  }, [selectedBranch.id, userBranchId, branchOptions]);

  const handleUserTagsMultiSelect = (selectedOpts: any) => {
    formikSetFieldValue("selectedUserTags", selectedOpts);
  };

  const handleMarketingTagsMultiSelect = (selectedOpts: any) => {
    formikSetFieldValue("selectedMarketingTags", selectedOpts);
  };

  const textPrimary =
    primary_text_button && ticketCount ? (
      <>
        {primary_text_button}
        <span className="bg-white rounded-xl text-black px-2 m-1">
          {ticketCount}
        </span>
      </>
    ) : (
      `${primary_text_button}`
    );

  const textSecondary =
    secondary_text_button && taskCount ? (
      <>
        {secondary_text_button}
        <span className="bg-white rounded-xl text-black px-2 m-1">
          {taskCount}
        </span>
      </>
    ) : (
      `${secondary_text_button}`
    );

  return (
    <Fragment>
      <div>
        <div className="w-full flex-col flex-1 justify-between md:flex-row flex gap-2">
          <div className="flex flex-1 flex-col md:flex-row gap-3 w-full max-w-3xl">
            <InputWithCornerHint
              label="Nombre"
              span=""
              id="name-filter"
              name="name-filter"
              type="text"
              className="w-full"
              placeholder="Nombre / Correo / DNI"
              value={search}
              onChange={(event) =>
                handleChangeValue("search", event.target.value)
              }
            />

            <Select
              selected={selectedGymMemo}
              setSelected={(gym) => {
                handleChangeValue("gym", gym);
              }}
              options={companyOptions}
              tittle="Compañía"
            />

            <Select
              selected={selectedBranchMemo}
              setSelected={(branch) => {
                handleChangeValue("branch", branch);
              }}
              options={branchOptions}
              tittle="Filial"
              disabled={isLoadingBranches}
            />
          </div>
          <div className="flex items-center justify-between flex-1 gap-2">
            <div className="flex items-center gap-x-4 pt-4">
              <button
                type="button"
                className="text-gray-400 hover:text-gray-500"
                onClick={() => handleChangeValue("isOpenFilter", !isOpenFilter)}
              >
                <span className="sr-only">View KPIs</span>
                <FontAwesomeIcon icon={faFilter} className="h-6 w-6" />
              </button>

              <button
                type="button"
                onClick={exportData}
                className=" text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Export</span>
                <FontAwesomeIcon icon={faCloudArrowDown} className="h-6 w-6" />
              </button>
            </div>
            <div className="flex gap-2">
              {primary_text_button ? (
                <ButtonPrimary
                  size="large"
                  text={textPrimary}
                  onClick={handleClickPrimaryButton}
                />
              ) : null}

              {secondary_text_button ? (
                <ButtonPrimary
                  size="large"
                  text={textSecondary}
                  onClick={handleClickSecondaryButton}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <Dialog
        open={isOpenFilter}
        onClose={() => handleChangeValue("isOpenFilter", !isOpenFilter)}
        className="relative z-50"
      >
        <DialogBackdrop className="fixed inset-0 bg-black opacity-50" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="bg-white w-full max-w-4xl rounded-2xl shadow-xl">
            <div className="p-4 flex flex-col w-full">
              <div className="flex flex-1 justify-between items-center">
                <h2 className="text-gray-500">Filtros avanzados de búsqueda</h2>
                <button
                  onClick={() =>
                    handleChangeValue("isOpenFilter", !isOpenFilter)
                  }
                  className="text-gray-500 hover:text-gray-700 self-end"
                >
                  <X />
                </button>
              </div>

              <div className="md:p-4">
                <form className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 pr-2">
                  <Select
                    selected={selectedGender}
                    setSelected={(option) => {
                      handleChangeValue("selectedGender", option);
                    }}
                    options={genderOptions}
                    tittle="Sexo"
                  />

                  <InputWithCornerHint
                    label="Edades mayores a"
                    span=""
                    id="minimalAge"
                    name="minimalAge"
                    type="text"
                    placeholder="Edad minima"
                    value={minimalAge}
                    onChange={(event) => {
                      const value = event.target.value.replace(/[^0-9]/g, "");
                      handleChangeValue("minimalAge", value);
                    }}
                    error={false}
                    textError=""
                  />
                  <InputWithCornerHint
                    label="Edades menores a"
                    span=""
                    id="maxAge"
                    name="maxAge"
                    type="text"
                    placeholder="Edad maxima"
                    value={maxAge}
                    onChange={(event) => {
                      const value = event.target.value.replace(/[^0-9]/g, "");
                      handleChangeValue("maxAge", value);
                    }}
                    error={false}
                    textError=""
                  />

                  {currentTab === "recovery_old" ? (
                    <>
                      <InputWithCornerHint
                        label="Desde"
                        span=""
                        id="dateInit"
                        name="dateInit"
                        type="date"
                        placeholder="Días de retraso"
                        value={delayDays}
                        onChange={(event) => {
                          handleChangeValue("dateInit", event);
                        }}
                        error={false}
                        textError=""
                      />
                      <InputWithCornerHint
                        label="Hasta"
                        span=""
                        id="dateEnd"
                        name="dateEnd"
                        type="date"
                        placeholder="Días de retraso"
                        value={delayDays}
                        onChange={(event) => {
                          handleChangeValue("dateEnd", event);
                        }}
                        error={false}
                        textError=""
                      />
                    </>
                  ) : (
                    <></>
                  )}

                  {currentTab === "recovery_latest" ? (
                    <>
                      <InputWithCornerHint
                        label="Días de retraso"
                        span=""
                        id="delayDays"
                        name="delayDays"
                        type="text"
                        placeholder="Días de retraso"
                        value={delayDays}
                        onChange={(event) => {
                          const value = event.target.value.replace(
                            /[^0-9]/g,
                            ""
                          );
                          handleChangeValue("delayDays", value);
                        }}
                        error={false}
                        textError=""
                      />
                      <Select
                        addDefault={false}
                        selected={selectedPaymentInfo}
                        setSelected={(option) =>
                          handleChangeValue("selectedPaymentInfo", option)
                        }
                        options={paymentInfoOptions}
                        tittle="Información de pago"
                      />
                    </>
                  ) : (
                    <></>
                  )}

                  <MultiSelect
                    options={userTagOptions}
                    selectedOptions={selectedUserTags}
                    setSelectedOptions={handleUserTagsMultiSelect}
                    title="Etiquetas usuario"
                    multiple={true}
                  />
                  <MultiSelect
                    options={userMarketingTagOptions}
                    selectedOptions={selectedMarketingTags}
                    setSelectedOptions={handleMarketingTagsMultiSelect}
                    title="Etiquetas marketing"
                    multiple={true}
                  />
                </form>
              </div>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </Fragment>
  );
};
